<template>
  <form @submit.prevent="submitForm" class="quote-form">
    <!-- Dados de Contato -->
    <div class="form-section">
      <h3 class="section-title">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
        </svg>
        Dados de Contato
      </h3>

      <div class="form-grid">
        <div class="form-group">
          <label for="whatsapp">WhatsApp *</label>
          <input id="whatsapp" v-model="formData.whatsapp" type="text" :class="{ 'error': $v.whatsapp.$error }" @blur="$v.whatsapp.$touch" @input="formData.whatsapp = formatWhatsApp($event.target.value)" placeholder="(11) 99999-9999" maxlength="15" />
          <span v-if="$v.whatsapp.$error" class="error-message">
            WhatsApp é obrigatório
          </span>
        </div>

        <div class="form-group">
          <label for="email">E-mail *</label>
          <input id="email" v-model="formData.email" type="email" :class="{ 'error': $v.email.$error }" @blur="$v.email.$touch" placeholder="Digite seu e-mail" />
          <span v-if="$v.email.$error" class="error-message">
            <span v-if="$v.email.required.$invalid">E-mail é obrigatório</span>
            <span v-else-if="$v.email.email.$invalid">E-mail inválido</span>
          </span>
        </div>
      </div>
    </div>

    <!-- Dados Pessoais -->
    <div class="form-section">
      <h3 class="section-title">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
        Dados Pessoais
      </h3>

      <div class="form-grid">
        <div class="form-group">
          <label for="name">Nome Completo *</label>
          <input id="name" v-model="formData.name" type="text" :class="{ 'error': $v.name.$error }" @blur="$v.name.$touch" placeholder="Digite seu nome completo" />
          <span v-if="$v.name.$error" class="error-message">
            Nome é obrigatório
          </span>
        </div>

        <div class="form-group">
          <label for="cpf">CPF *</label>
          <input id="cpf" v-model="formData.cpf" type="text" :class="{ 'error': $v.cpf.$error }" @blur="$v.cpf.$touch" @input="formData.cpf = formatCpf($event.target.value)" placeholder="000.000.000-00" maxlength="14" />
          <span v-if="$v.cpf.$error" class="error-message">
            <span v-if="$v.cpf.required.$invalid">CPF é obrigatório</span>
            <span v-else-if="$v.cpf.cpfValidator.$invalid">CPF deve ter 11 dígitos</span>
          </span>
        </div>

        <div class="form-group">
          <label for="cnhNumber">Número da CNH *</label>
          <input id="cnhNumber" v-model="formData.cnhNumber" type="text" :class="{ 'error': $v.cnhNumber.$error }" @blur="$v.cnhNumber.$touch" placeholder="Digite o número da CNH" />
          <span v-if="$v.cnhNumber.$error" class="error-message">
            Número da CNH é obrigatório
          </span>
        </div>

        <div class="form-group">
          <label for="cnhExpiration">Validade da CNH *</label>
          <flatpickr id="cnhExpiration" v-model="formData.cnhExpiration" :class="{ 'error': $v.cnhExpiration.$error }" @blur="$v.cnhExpiration.$touch" :config="dateConfig" placeholder="Selecione a data de validade" />
          <span v-if="$v.cnhExpiration.$error" class="error-message">
            Data de validade da CNH é obrigatória
          </span>
        </div>

        <div class="form-group">
          <label for="cnhIssueDate">Data de Emissão da CNH *</label>
          <flatpickr id="cnhIssueDate" v-model="formData.cnhIssueDate" :class="{ 'error': $v.cnhIssueDate.$error }" @blur="$v.cnhIssueDate.$touch" :config="dateConfig" placeholder="Selecione a data de emissão" />
          <span v-if="$v.cnhIssueDate.$error" class="error-message">
            Data de emissão da CNH é obrigatória
          </span>
        </div>

        <div class="form-group">
          <label for="sex">Sexo *</label>
          <select id="sex" v-model.number="formData.sex" :class="{ 'error': $v.sex.$error }" @blur="$v.sex.$touch">
            <option value="1">Masculino</option>
            <option value="2">Feminino</option>
          </select>
          <span v-if="$v.sex.$error" class="error-message">
            Sexo é obrigatório
          </span>
        </div>

        <div class="form-group">
          <label for="birthDate">Data de Nascimento *</label>
          <flatpickr id="birthDate" v-model="formData.birthDate" :class="{ 'error': $v.birthDate.$error }" @blur="$v.birthDate.$touch" :config="dateConfig" placeholder="Selecione a data de nascimento" />
          <span v-if="$v.birthDate.$error" class="error-message">
            Data de nascimento é obrigatória
          </span>
        </div>

        <div class="form-group">
          <label for="zipCodeHome">CEP *</label>
          <input id="zipCodeHome" v-model="formData.zipCodeHome" type="text" :class="{ 'error': $v.zipCodeHome.$error }" @blur="handleCepBlur" @input="formData.zipCodeHome = formatCep($event.target.value)" placeholder="00000-000" maxlength="9" />
          <span v-if="$v.zipCodeHome.$error" class="error-message">
            CEP é obrigatório
          </span>
        </div>

        <div class="form-group checkbox-group">
          <label class="checkbox-label">
            <input v-model="formData.hasEarlyAgeDriver" type="checkbox" />
            <span class="checkmark"></span>
            Possui condutor menor de idade
          </label>
        </div>
      </div>
    </div>

    <!-- Dados do Veículo -->
    <div class="form-section">
      <h3 class="section-title">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
          <line x1="8" y1="21" x2="16" y2="21"></line>
          <line x1="12" y1="17" x2="12" y2="21"></line>
        </svg>
        Dados do Veículo
      </h3>

      <div class="form-grid">
        <div class="form-group">
          <label for="licensePlate">Placa do Veículo *</label>
          <input id="licensePlate" v-model="formData.licensePlate" type="text" :class="{ 'error': $v.licensePlate.$error }" @blur="$v.licensePlate.$touch" @input="formData.licensePlate = formatLicensePlate($event.target.value)" placeholder="ABC-1234" maxlength="8" />
          <span v-if="$v.licensePlate.$error" class="error-message">
            Placa do veículo é obrigatória
          </span>
        </div>

        <div class="form-group">
          <label for="renavam">RENAVAM *</label>
          <input id="renavam" v-model="formData.renavam" type="text" :class="{ 'error': $v.renavam.$error }" @blur="$v.renavam.$touch" @input="formData.renavam = $event.target.value.replace(/\D/g, '')" placeholder="Digite o RENAVAM" />
          <span v-if="$v.renavam.$error" class="error-message">
            RENAVAM é obrigatório
          </span>
        </div>

        <div class="form-group">
          <label for="chassis">Chassi *</label>
          <input id="chassis" v-model="formData.chassis" type="text" :class="{ 'error': $v.chassis.$error }" @blur="$v.chassis.$touch" placeholder="Digite o chassi" />
          <span v-if="$v.chassis.$error" class="error-message">
            Chassi é obrigatório
          </span>
        </div>

        <div class="form-group">
          <label for="brand">Marca *</label>
          <select id="brand" v-model="formData.brand" :class="{ 'error': $v.brand.$error }" @blur="$v.brand.$touch">
            <option value="">Selecione a marca</option>
            <option v-for="brand in brands" :key="brand" :value="brand">
              {{ brand }}
            </option>
          </select>
          <span v-if="$v.brand.$error" class="error-message">
            Marca é obrigatória
          </span>
        </div>

        <div class="form-group">
          <label for="model">Modelo *</label>
          <input id="model" v-model="formData.model" type="text" :class="{ 'error': $v.model.$error }" @blur="$v.model.$touch" placeholder="Digite o modelo" />
          <span v-if="$v.model.$error" class="error-message">
            Modelo é obrigatório
          </span>
        </div>

        <div class="form-group">
          <label for="year">Ano *</label>
          <input id="year" v-model="formData.year" type="text" :class="{ 'error': $v.year.$error }" @blur="$v.year.$touch" @input="formData.year = $event.target.value.replace(/[^0-9]/g, '').slice(0, 4)" placeholder="2024" maxlength="4" />
          <span v-if="$v.year.$error" class="error-message">
            Ano é obrigatório e deve estar entre 1900 e {{ new Date().getFullYear() + 1 }}
          </span>
        </div>

        <div class="form-group">
          <label for="color">Cor *</label>
          <select id="color" v-model="formData.color" :class="{ 'error': $v.color.$error }" @blur="$v.color.$touch">
            <option value="">Selecione a cor</option>
            <option v-for="color in colors" :key="color" :value="color">
              {{ color }}
            </option>
          </select>
          <span v-if="$v.color.$error" class="error-message">
            Cor é obrigatória
          </span>
        </div>

        <div class="form-group">
          <label for="fuelType">Tipo de Combustível *</label>
          <select id="fuelType" v-model="formData.fuelType" :class="{ 'error': $v.fuelType.$error }" @blur="$v.fuelType.$touch">
            <option value="">Selecione</option>
            <option value="Gasolina">Gasolina</option>
            <option value="Álcool">Álcool</option>
            <option value="Flex">Flex</option>
            <option value="Diesel">Diesel</option>
            <option value="Elétrico">Elétrico</option>
            <option value="Híbrido">Híbrido</option>
          </select>
          <span v-if="$v.fuelType.$error" class="error-message">
            Tipo de combustível é obrigatório
          </span>
        </div>

        <div class="form-group">
          <label for="vehicleType">Tipo de Veículo *</label>
          <select id="vehicleType" v-model.number="formData.vehicleType" :class="{ 'error': $v.vehicleType.$error }" @blur="$v.vehicleType.$touch">
            <option value="">Selecione</option>
            <option value="1">Carro</option>
            <option value="2">Moto</option>
            <option value="3">Caminhão</option>
            <option value="4">Ônibus</option>
            <option value="5">Van</option>
          </select>
          <span v-if="$v.vehicleType.$error" class="error-message">
            Tipo de veículo é obrigatório
          </span>
        </div>

        <div class="form-group">
          <label for="fipeValue">Valor FIPE *</label>
          <input id="fipeValue" v-model.number="formData.fipeValue" type="number" :class="{ 'error': $v.fipeValue.$error }" @blur="$v.fipeValue.$touch" step="0.01" min="0" placeholder="50000.00" />
          <span v-if="$v.fipeValue.$error" class="error-message">
            Valor FIPE é obrigatório
          </span>
        </div>

        <div class="form-group">
          <label for="usageType">Tipo de Uso *</label>
          <select id="usageType" v-model.number="formData.usageType" :class="{ 'error': $v.usageType.$error }" @blur="$v.usageType.$touch">
            <option value="">Selecione</option>
            <option value="1">Particular</option>
            <option value="2">Comercial</option>
            <option value="3">Táxi</option>
            <option value="4">Uber/99</option>
            <option value="5">Aluguel</option>
          </select>
          <span v-if="$v.usageType.$error" class="error-message">
            Tipo de uso é obrigatório
          </span>
        </div>

        <div class="form-group checkbox-group">
          <label class="checkbox-label">
            <input v-model="formData.isZeroKm" type="checkbox" />
            <span class="checkmark"></span>
            Veículo zero quilômetro
          </label>
        </div>
      </div>
    </div>

    <!-- CAPTCHA -->
    <div class="form-section">
      <h3 class="section-title">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
          <circle cx="12" cy="16" r="1"></circle>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
        </svg>
        Verificação de Segurança
      </h3>

      <TurnstileCaptcha :site-key="siteKey" @success="onCaptchaSuccess" @error="onCaptchaError" @expired="onCaptchaExpired" />
    </div>

    <!-- Botões -->
    <div class="form-actions">
      <button type="button" @click="clearForm" class="btn-secondary">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="3,6 5,6 21,6"></polyline>
          <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
        </svg>
        Limpar Formulário
      </button>

      <button type="submit" :disabled="loading" class="btn-primary">
        <svg v-if="loading" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner">
          <line x1="12" y1="2" x2="12" y2="6"></line>
          <line x1="12" y1="18" x2="12" y2="22"></line>
          <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
          <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
          <line x1="2" y1="12" x2="6" y2="12"></line>
          <line x1="18" y1="12" x2="22" y2="12"></line>
          <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
          <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
        </svg>
        {{ loading ? 'Enviando...' : 'Solicitar Cotação' }}
      </button>
    </div>
  </form>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { minLength, minValue, required } from '@vuelidate/validators'
import 'flatpickr/dist/flatpickr.css'
import { v4 as uuidv4 } from 'uuid'
import { computed, reactive } from 'vue'
import flatpickr from 'vue-flatpickr-component'
import api from '../services/api'
import TurnstileCaptcha from './TurnstileCaptcha.vue'

export default {
  name: 'QuoteForm',
  components: {
    flatpickr,
    TurnstileCaptcha
  },
  props: {
    serial: {
      type: String,
      required: true
    },
    siteKey: {
      type: String,
      required: true
    }
  },
  emits: ['success', 'error'],
  setup(props, { emit }) {
    const loading = reactive({ value: false })
    const captchaToken = reactive({ value: null })

    const formData = reactive({
      key: props.serial,
      peopleBo__Ide: uuidv4(),
      name: '',
      cpf: '',
      cnhNumber: '',
      cnhExpiration: '',
      cnhIssueDate: '',
      sex: 1,
      birthDate: '',
      email: '',
      whatsapp: '',
      zipCodeHome: '',
      hasEarlyAgeDriver: false,
      licensePlate: '',
      renavam: '',
      chassis: '',
      brand: '',
      model: '',
      year: '',
      color: '',
      fuelType: '',
      vehicleType: 1,
      fipeValue: '',
      isZeroKm: false,
      usageType: 1
    })

    const brands = [
      'Volkswagen', 'Fiat', 'Ford', 'Chevrolet', 'Renault', 'Peugeot',
      'Citroën', 'Nissan', 'Honda', 'Toyota', 'Hyundai', 'Kia',
      'Mitsubishi', 'Subaru', 'Mazda', 'Suzuki', 'Audi', 'BMW',
      'Mercedes-Benz', 'Volvo', 'Land Rover', 'Jaguar', 'Porsche',
      'Ferrari', 'Lamborghini', 'Maserati', 'Bentley', 'Rolls-Royce',
      'Aston Martin', 'McLaren', 'Lotus', 'Alfa Romeo', 'Lancia',
      'Saab', 'Opel', 'Seat', 'Skoda', 'Dacia', 'Lada'
    ]

    const colors = [
      'Branco', 'Preto', 'Prata', 'Cinza', 'Azul', 'Vermelho',
      'Verde', 'Amarelo', 'Laranja', 'Marrom', 'Bege', 'Dourado',
      'Rosa', 'Roxo', 'Outro'
    ]

    const dateConfig = {
      dateFormat: 'd/m/Y',
      locale: 'pt'
    }

    // Validação customizada para CPF (11 dígitos numéricos)
    const cpfValidator = (value) => {
      if (!value) return false
      const cpfDigits = value.replace(/\D/g, '') // Remove máscara
      return cpfDigits.length === 11
    }

    // Função para formatar CPF
    const formatCpf = (value) => {
      const cpf = value.replace(/\D/g, '')
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    }

    // Função para formatar CEP
    const formatCep = (value) => {
      const cep = value.replace(/\D/g, '')
      return cep.replace(/(\d{5})(\d{3})/, '$1-$2')
    }

    // Função para formatar placa (alfanumérico)
    const formatLicensePlate = (value) => {
      const plate = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
      if (plate.length <= 3) return plate
      return plate.replace(/([A-Za-z0-9]{3})([A-Za-z0-9]{4})/, '$1-$2')
    }

    // Função para formatar WhatsApp
    const formatWhatsApp = (value) => {
      const numbers = value.replace(/\D/g, '')
      if (numbers.length <= 2) return numbers
      if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`
    }

    const rules = {
      name: { required },
      cpf: {
        required,
        cpfValidator
      },
      cnhNumber: { required },
      cnhExpiration: { required },
      cnhIssueDate: { required },
      sex: {
        required,
        minValue: minValue(1)
      },
      birthDate: { required },
      email: {
        required,
        email: (value) => {
          if (!value) return true
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          return emailRegex.test(value)
        }
      },
      whatsapp: { required },
      zipCodeHome: {
        required,
        minLength: minLength(9) // 00000-000
      },
      licensePlate: { required },
      renavam: { required },
      chassis: { required },
      brand: { required },
      model: { required },
      year: {
        required,
        minLength: minLength(4)
      },
      fuelType: { required },
      vehicleType: {
        required,
        minValue: minValue(1)
      },
      color: { required },
      fipeValue: {
        required,
        minValue: minValue(0)
      },
      usageType: {
        required,
        minValue: minValue(1)
      }
    }

    const $v = useVuelidate(rules, formData)

    const isFormValid = computed(() => {
      return !$v.value.$invalid
    })

    const handleCepBlur = async () => {
      if (formData.zipCodeHome.length === 9) {
        await fetchAddressFromCep()
      }
    }

    const fetchAddressFromCep = async () => {
      try {
        const cep = formData.zipCodeHome.replace(/\D/g, '')
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
        const data = await response.json()

        if (!data.erro) {
          // Aqui você pode preencher campos de endereço se necessário
          console.log('Endereço encontrado:', data)
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error)
      }
    }

    const onCaptchaSuccess = (token) => {
      captchaToken.value = token
    }

    const onCaptchaError = () => {
      captchaToken.value = null
    }

    const onCaptchaExpired = () => {
      captchaToken.value = null
    }

    const clearForm = () => {
      Object.assign(formData, {
        key: props.serial,
        peopleBo__Ide: uuidv4(),
        name: '',
        cpf: '',
        cnhNumber: '',
        cnhExpiration: '',
        cnhIssueDate: '',
        sex: null,
        birthDate: '',
        email: '',
        whatsapp: '',
        zipCodeHome: '',
        hasEarlyAgeDriver: false,
        licensePlate: '',
        renavam: '',
        chassis: '',
        brand: '',
        model: '',
        year: '',
        color: '',
        fuelType: '',
        vehicleType: 1,
        fipeValue: 0,
        isZeroKm: false,
        usageType: 1
      })
      $v.value.$reset()
      captchaToken.value = null
    }

    const submitForm = async () => {
      // Validar todos os campos
      $v.value.$touch()

      // Verificar se há erros de validação
      if ($v.value.$invalid) {
        console.log('Erros de validação:', $v.value.$errors)
        alert('Por favor, preencha todos os campos obrigatórios corretamente.')
        return
      }

      // Verificar se o CAPTCHA foi resolvido
      if (!captchaToken.value) {
        alert('Por favor, resolva o CAPTCHA para continuar.')
        return
      }

      loading.value = true

      try {
        // Função para converter data brasileira (DD/MM/YYYY) para ISO (YYYY-MM-DD)
        const convertToISODate = (dateStr) => {
          if (!dateStr) return null
          const parts = dateStr.split('/')
          if (parts.length !== 3) return dateStr
          // DD/MM/YYYY -> YYYY-MM-DD
          return `${parts[2]}-${parts[1]}-${parts[0]}`
        }

        const payload = {
          key: formData.key,
          peopleBo__Ide: formData.peopleBo__Ide,
          name: formData.name,
          cpf: formData.cpf,
          cnhNumber: formData.cnhNumber,
          cnhExpiration: convertToISODate(formData.cnhExpiration),
          cnhIssueDate: convertToISODate(formData.cnhIssueDate),
          sex: formData.sex,
          birthDate: convertToISODate(formData.birthDate),
          email: formData.email,
          WhatsApp: formData.whatsapp,
          zipCodeHome: formData.zipCodeHome,
          hasEarlyAgeDriver: formData.hasEarlyAgeDriver,
          licensePlate: formData.licensePlate,
          renavam: formData.renavam,
          chassis: formData.chassis,
          brand: formData.brand,
          model: formData.model,
          year: parseInt(formData.year),
          color: formData.color,
          fuelType: formData.fuelType,
          vehicleType: formData.vehicleType,
          fipeValue: formData.fipeValue,
          isZeroKm: formData.isZeroKm,
          usageType: formData.usageType,
          captchaToken: captchaToken.value
        }

        const response = await api.post('/vehicle-insurance/quote', payload)
        emit('success', response.data)
      } catch (error) {
        console.error('Erro ao enviar cotação:', error)
        alert('Erro ao enviar cotação. Tente novamente.')
      } finally {
        loading.value = false
      }
    }

    return {
      formData,
      $v,
      loading: loading.value,
      captchaToken: captchaToken.value,
      brands,
      colors,
      dateConfig,
      isFormValid,
      formatCpf,
      formatCep,
      formatLicensePlate,
      formatWhatsApp,
      handleCepBlur,
      fetchAddressFromCep,
      onCaptchaSuccess,
      onCaptchaError,
      onCaptchaExpired,
      clearForm,
      submitForm
    }
  }
}
</script>

<style scoped>
.quote-form {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  color: #244179;
  font-size: 1.25rem;
  font-weight: 600;
  border-bottom: 2px solid #F7D810;
  padding-bottom: 0.5rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #244179;
  font-size: 0.875rem;
}

.form-group input,
.form-group select {
  padding: 0.75rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #244179;
  box-shadow: 0 0 0 3px rgba(36, 65, 121, 0.1);
}

.form-group input.error,
.form-group select.error {
  border-color: #dc3545;
}

.error-message {
  color: #dc3545;
  font-size: 0.75rem;
  font-weight: 500;
}

.checkbox-group {
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-weight: 500;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin: 0;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 2rem;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 2rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 180px;
  justify-content: center;
}

.btn-primary {
  background: #244179;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #365494;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(36, 65, 121, 0.3);
}

.btn-primary:disabled {
  background: #6c757d;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-secondary {
  background: #F7D810;
  color: #244179;
  border: 2px solid #F7D810;
}

.btn-secondary:hover {
  background: #e6c20e;
  border-color: #e6c20e;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(247, 216, 16, 0.3);
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .quote-form {
    padding: 1rem;
  }

  .form-section {
    padding: 1.5rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}
</style>
